import { BodyM, Button, colorVariants, Description } from '@casavo/base-ui'
import cn from 'classnames'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import React, { MutableRefObject } from 'react'

import HouseRent from '@/components/Icon/HouseRent'
import Mail from '@/components/Icon/Mail'
import Phone from '@/components/Icon/Phone'
import { LabelWithCheck } from '@/components/LabelWithCheck'
import { RequestContactCTA } from '@/components/RequestContactCTA'
import RevealButton from '@/components/RevealButton'
import { useDevice } from '@/context/Device'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ListingCate } from '@/page-commons/listingBySlug'
import { paddingBottom } from '@/shared-styles/styles.css'
import { CasavoInventory } from '@/types/listing'
import { anchorSmoothScroll } from '@/utils/dom-interactions-helpers'
import { isThirdParty } from '@/utils/listing/listingType'
import { handleListingDetailClick } from '@/utils/tracking/eventsHandlers'

import { CasavoAdvantage } from './CasavoAdvantage'
import {
  detailContactBox,
  detailContactPhone,
  detailContactsAgencyName,
  detailContactsOuter,
  detailContactsRequestContactCta,
} from './styles.css'

const getHeader = (inventory: string): string => {
  switch (inventory) {
    case CasavoInventory.Agency:
      return 'PropertyAdvantages:PropertyAdvantages_CasavoGuarantee_From_Title'
    case CasavoInventory.iBuyer:
      return 'listing-detail:at_your_service_casavo'
    case CasavoInventory.Third_Party:
      return 'listing-detail:at_your_service_agency'
  }
}

const DetailContacts: React.FC<{
  advantages: CasavoAdvantage[]
  clickShowNumberButtonHandler: VoidFunction
  property: ListingCate
  shouldShowPhoneNumber: boolean
  similarPropertiesRef: MutableRefObject<HTMLDivElement>
}> = ({ advantages, clickShowNumberButtonHandler, property, shouldShowPhoneNumber, similarPropertiesRef }) => {
  const { t } = useTranslation()
  const device = useDevice()

  const handleClickRequestInfo = (e: React.SyntheticEvent) => {
    e.preventDefault()
    anchorSmoothScroll('#contactForm', device === 'mobile' ? -80 : -100)
    handleListingDetailClick('RequestInfo')
  }

  const { lang } = useTranslation()

  const thirdParty = isThirdParty(property)
  const isPropriooListing = property.country === 'france'
  const shouldShowAgencyPhoneNumber =
    thirdParty &&
    !!property.contactInfo.agencyName &&
    !!property.contactInfo.telephoneNumberContact &&
    !isPropriooListing
  const listingTitle = property.detail.titles.find((title) => title.language === lang).text
  const inventoryHeader = getHeader(property.detail.inventory)

  return (
    <div className={detailContactBox} data-testid="detail-contact-box">
      <div className={thirdParty ? detailContactsOuter.thirdParty : detailContactsOuter.default}>
        <Trans
          components={[<p key="0" />, <span key="1" className={sprinkles({ fontWeight: 600 })} />]}
          i18nKey={inventoryHeader}
        />
      </div>
      {thirdParty && (
        <div className={paddingBottom.xl} data-testid="third-party-section">
          <BodyM className={detailContactsAgencyName}>
            <HouseRent
              className={sprinkles({
                display: 'inline-block',
                marginRight: 's-m',
                verticalAlign: 'baseline',
              })}
            />
            {property.contactInfo.agencyName}
          </BodyM>
          <Description color="greyscale500">{property.contactInfo.agencyAddress}</Description>
        </div>
      )}
      <div className={paddingBottom.l}>
        {advantages
          .filter((advantage) => t(advantage.titleTranslationKey) !== '')
          .map((advantage) => (
            <LabelWithCheck key={advantage.titleTranslationKey}>{t(advantage.titleTranslationKey)}</LabelWithCheck>
          ))}
      </div>
      <div className={detailContactsRequestContactCta}>
        {!thirdParty ? (
          <>
            <RequestContactCTA
              listing={property}
              listingTitle={listingTitle}
              requestType="info"
              similarPropertiesRef={similarPropertiesRef}
            />
            <RequestContactCTA
              data-testid="request-visit-button"
              listing={property}
              listingTitle={listingTitle}
              requestType="visit"
              similarPropertiesRef={similarPropertiesRef}
            />
          </>
        ) : (
          <>
            {shouldShowAgencyPhoneNumber && (
              <div className={detailContactPhone} data-testid="detailContacts-phone-contact">
                <Phone
                  className={cn(
                    colorVariants['greyscale600'],
                    sprinkles({
                      display: 'inline-block',
                      marginRight: 's',
                      marginTop: 'xs',
                    })
                  )}
                  height={20}
                  width={20}
                />
                <RevealButton
                  buttonLabel={t('listing-detail:show_number')}
                  clickRevealButtonHandler={clickShowNumberButtonHandler}
                  data-testid="agency-number-button"
                  shouldRevealContent={shouldShowPhoneNumber}
                >
                  <Description color="greyscale500">{property.contactInfo.telephoneNumberContact}</Description>
                </RevealButton>
              </div>
            )}
            <Button
              fullWidth
              data-testid={'request-info-cta'}
              Icon={(props) => <Mail {...props} height={20} width={20} />}
              label={t('listing-detail:request_info')}
              styleVariant="primary"
              onClick={handleClickRequestInfo}
            />
          </>
        )}
      </div>
    </div>
  )
}
export default DetailContacts
